import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import { ArrowsRotateSpinIcon } from '@repo/icons';
import { getLabels } from '../labels';
const createOutlineVariant = ({ text, border, background, ring, }) => `${text} border ${border} ${background} hover:text-white focus:text-white focus:ring-2 ${ring}`;
const colorStyles = {
    brand: {
        solid: 'bg-brand text-white hover:bg-brand-strong focus:bg-brand-strong focus:ring-2 focus:ring-brand-medium',
    },
    secondary: {
        solid: 'bg-secondary-medium border border-stroke-base-medium text-body hover:bg-tertiary-medium hover:text-heading focus:bg-tertiary-medium focus:text-heading focus:ring-2 focus:ring-tertiary',
    },
    tertiary: {
        solid: 'bg-primary-soft text-body border border-stroke-base hover:bg-secondary-medium hover:text-heading focus:bg-secondary-soft focus:text-heading focus:ring-2 focus:ring-tertiary',
    },
    success: {
        solid: 'bg-success text-white hover:bg-success-strong focus:bg-success-strong focus:ring-2 focus:ring-stroke-success-subtle',
        outline: createOutlineVariant({
            text: 'text-success',
            border: 'border-stroke-success',
            background: 'hover:bg-success-strong focus:bg-success-strong',
            ring: 'focus:ring-stroke-success-subtle',
        }),
    },
    danger: {
        solid: 'bg-danger text-white hover:bg-danger-strong focus:bg-danger-strong focus:ring-2 focus:ring-stroke-danger-subtle',
        outline: createOutlineVariant({
            text: 'text-danger',
            border: 'border-stroke-danger',
            background: 'hover:bg-danger-strong focus:bg-danger-strong',
            ring: 'focus:ring-stroke-danger-subtle',
        }),
    },
    warning: {
        solid: 'bg-warning text-white hover:bg-warning-strong focus:bg-warning-strong focus:ring-2 focus:ring-stroke-warning-subtle',
        outline: createOutlineVariant({
            text: 'text-warning',
            border: 'border-stroke-warning',
            background: 'hover:bg-warning-strong focus:bg-warning-strong',
            ring: 'focus:ring-stroke-warning-subtle',
        }),
    },
    dark: {
        solid: 'bg-dark text-white hover:bg-dark-strong focus:bg-dark-strong focus:ring-2 focus:ring-stroke-base',
    },
    ghost: {
        solid: 'text-heading hover:bg-secondary-medium hover:text-heading focus:bg-secondary-medium focus:text-heading focus:ring-2 focus:ring-stroke-light',
    },
};
const disabledStyles = 'bg-disabled border text-fg-disabled cursor-not-allowed';
const iconOnlyGapStyle = 'gap-1.5';
const sizeStyles = {
    xs: { base: 'px-3 py-1.5 text-xs leading-5', iconOnly: 'p-1.5', icon: 'size-3.5' },
    sm: { base: 'px-3 py-2 text-sm leading-5', iconOnly: 'p-2', icon: 'size-4' },
    base: { base: 'px-4 py-2.5 text-sm leading-5', iconOnly: 'p-2.5', icon: 'size-4' },
    lg: { base: 'px-5 py-3 text-base leading-6', iconOnly: 'p-3', icon: 'size-5' },
    xl: { base: 'px-6 py-3.5 text-base leading-6', iconOnly: 'p-3.5', icon: 'size-5' },
};
export default function Button({ testId, label, color = 'brand', size = 'base', variant = 'solid', leftIcon: LeftIcon, rightIcon: RightIcon, disabled = false, pending = false, iconOnly = false, grouped, type = 'button', children, className, ...buttonProps }) {
    const { pendingLabel } = getLabels().button;
    if (pending) {
        disabled = true;
        if (label)
            label = pendingLabel;
        LeftIcon = ArrowsRotateSpinIcon;
        RightIcon = undefined;
    }
    const baseClasses = 'inline-flex items-center justify-center';
    const hasIcon = Boolean(LeftIcon || RightIcon);
    const content = children ?? label;
    const sizeClass = iconOnly ? sizeStyles[size].iconOnly : sizeStyles[size].base;
    const iconGapClass = hasIcon && !iconOnly ? iconOnlyGapStyle : '';
    const colorVariantStyles = colorStyles[color];
    const colorClass = disabled ? disabledStyles : (colorVariantStyles[variant] ?? colorVariantStyles.solid);
    const groupedClass = grouped ? 'rounded-none' : 'rounded-xl shadow-button';
    const buttonClasses = classNames(baseClasses, disabled ? '' : 'cursor-pointer', colorClass, sizeClass, iconGapClass, groupedClass, className);
    return (_jsxs("button", { type: type, className: buttonClasses, disabled: disabled, "data-testid": testId, ...buttonProps, children: [LeftIcon && _jsx(LeftIcon, { className: classNames(sizeStyles[size].icon, 'shrink-0') }), !iconOnly && content && _jsx("span", { className: "min-w-0 text-left font-sans font-medium", children: content }), RightIcon && _jsx(RightIcon, { className: classNames(sizeStyles[size].icon, 'shrink-0') })] }));
}
